import React from 'react';
import { Check } from 'react-feather';
import DocumentProgressItem from './DocumentProgressItem/DocumentProgressItem';
import DocumentFailedItem from './DocumentFailedItem/DocumentFailedItem';
import ActionButtons from './ActionButtons/ActionButtons';
import Button from '~/src/components/Button/Button';
import { DocumentStatus } from './types';

const ConversionStatusCard = ({
  jobData,
  onSelectMoreDocuments,
  onCancelCreation,
  isCancelling,
}: {
  jobData: any;
  onSelectMoreDocuments?: () => void;
  onCancelCreation?: () => void;
  isCancelling?: boolean;
}) => {
  if (!jobData) {
    return null;
  }

  const { documents } = jobData;

  const pendingProcessingDocuments = (documents || []).filter(
    (doc: any) =>
      doc.status === DocumentStatus.PENDING ||
      doc.status === DocumentStatus.PROCESSING,
  );

  const failedDocuments = (documents || []).filter(
    (doc: any) => doc.status === DocumentStatus.FAILED,
  );

  const completedDocuments = (documents || []).filter(
    (doc: any) => doc.status === DocumentStatus.COMPLETED,
  );

  const hasActiveProcessing = pendingProcessingDocuments.length > 0;
  const allComplete =
    completedDocuments.length > 0 &&
    !hasActiveProcessing &&
    failedDocuments.length === 0;
  const allFailed =
    failedDocuments.length > 0 &&
    !hasActiveProcessing &&
    completedDocuments.length === 0;

  const getTitle = () => {
    if (allComplete) {
      return 'Templates created';
    }
    if (failedDocuments.length > 0 && !hasActiveProcessing) {
      return 'Creation incomplete';
    }
    return 'Creation in progress';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-2xl mx-auto flex flex-col gap-8">
      <div>
        <h2 className="text-base font-semibold text-gray-900 m-0 flex items-center gap-2">
          {allComplete && <Check className="text-green-500" size={20} />}
          {getTitle()}
        </h2>
      </div>
      {!allComplete && (
        <>
          {failedDocuments.length === 0 && (
            <div className="text-sm">
              We will send you an email once all templates are ready. Feel free
              to navigate away from this page.
            </div>
          )}
          <div className="flex flex-col gap-2">
            {pendingProcessingDocuments.map((doc: any, index: number) => (
              <DocumentProgressItem
                key={`processing-${doc.filename}-${doc.id || index}`}
                filename={doc.filename}
                status={doc.status as DocumentStatus}
              />
            ))}
            {failedDocuments.map((doc: any, index: number) => (
              <DocumentFailedItem
                key={`failed-${doc.filename}-${doc.id || index}`}
                document={doc}
              />
            ))}
          </div>
          {failedDocuments.length === 0 && (
            <div className="text-sm text-gray-500">
              You can select more documents once all templates are created from
              the current selection.
            </div>
          )}
          {hasActiveProcessing && onCancelCreation && (
            <div className="flex justify-end">
              <Button
                secondary
                className="h-8 text-xs font-medium"
                onClick={onCancelCreation}
                disabled={isCancelling}
                tabIndex={0}
                aria-label="Cancel creation"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel creation'}
              </Button>
            </div>
          )}
        </>
      )}
      {allFailed && (
        <ActionButtons
          allProcessed={true}
          onSelectMoreDocuments={onSelectMoreDocuments}
        />
      )}
    </div>
  );
};

export default ConversionStatusCard;
